import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Download, Globe2, Flame, ShoppingBasket, Lock, Sparkles } from "lucide-react";
import Nav from "@/components/site/Nav";
import { API_BASE } from "@/lib/api";
import { INDIAN_REGIONS } from "@/data/diet";

const SLOTS = ["breakfast", "lunch", "snack", "dinner"];
const SLOT_LABEL = { breakfast: "Breakfast", lunch: "Lunch", snack: "Snack", dinner: "Dinner" };

const MacroChips = ({ m }) => (
  <div className="flex flex-wrap gap-1.5 text-[10px] font-semibold">
    <span className="rounded-full bg-[#C57B57]/15 text-[#AD6A48] px-2 py-0.5">{m.kcal} kcal</span>
    <span className="rounded-full bg-[#2D372B]/10 text-[#2D372B] px-2 py-0.5">P {m.protein}g</span>
    <span className="rounded-full bg-[#89937C]/15 text-[#5C665A] px-2 py-0.5">C {m.carbs}g</span>
    <span className="rounded-full bg-[#89937C]/15 text-[#5C665A] px-2 py-0.5">F {m.fat}g</span>
  </div>
);

export default function GlobalSeries() {
  const [params, setParams] = useSearchParams();
  const [list, setList] = useState(null);
  const [festList, setFestList] = useState([]);
  const [member, setMember] = useState(undefined);
  const [detail, setDetail] = useState(null);
  const [day, setDay] = useState(1);
  const [loading, setLoading] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const selected = params.get("c") || "";
  const region = params.get("r") || "";
  const festSel = params.get("f") || "";

  useEffect(() => {
    axios.get(`${API_BASE}/diet/global-series`).then((r) => setList(r.data)).catch(() => setList([]));
    axios.get(`${API_BASE}/diet/festivals`).then((r) => setFestList(r.data)).catch(() => {});
    axios.get(`${API_BASE}/member/auth/me`, { withCredentials: true }).then((r) => setMember(r.data)).catch(() => setMember(null));
  }, []);

  useEffect(() => {
    if ((!selected && !festSel) || !member) { setDetail(null); return; }
    setLoading(true);
    const url = festSel
      ? `${API_BASE}/diet/festivals/${festSel}`
      : `${API_BASE}/diet/global-series/${selected}${region ? `?region=${region}` : ""}`;
    axios.get(url, { withCredentials: true })
      .then((r) => { setDetail(r.data); setDay(1); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load plan"))
      .finally(() => setLoading(false));
  }, [selected, region, festSel, member]);

  const pick = (id) => setParams({ c: id });

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const src = festSel
        ? `${API_BASE}/diet/festivals/${festSel}/pdf`
        : `${API_BASE}/diet/global-series/${selected}/pdf${region ? `?region=${region}` : ""}`;
      const r = await axios.get(src, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `JAY7_${(detail?.short || festSel || selected).replace(/[^A-Za-z0-9]+/g, "_")}_Plan.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Your branded 7-day plan + grocery list is downloading");
    } catch {
      toast.error("Could not download PDF");
    } finally { setPdfBusy(false); }
  };

  const activeDay = detail?.plan?.find((d) => d.day === day);

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#2D372B]">
      <Nav />
      <div className="max-w-6xl mx-auto px-6 pt-32 pb-24" data-testid="global-series-page">
        <div className="text-xs font-bold tracking-[0.2em] text-[#C57B57] uppercase flex items-center gap-2"><Globe2 size={13} /> JAY 7™ Global Series</div>
        <h1 className="mt-2 font-serif-display text-4xl sm:text-5xl">One world. Seven cuisines.<br />One fat-loss engine.</h1>
        <p className="mt-3 text-sm text-[#5C665A] max-w-xl">Pick your cuisine — every plan is a calorie-tracked 7-day fat-loss week (~1,400–1,550 kcal, protein-first) with a downloadable branded plan and grocery list.</p>

        {!list ? (
          <div className="py-16 text-center"><Loader2 className="animate-spin inline text-[#89937C]" /></div>
        ) : (
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="cuisine-selector">
            {list.map((c) => (
              <button key={c.id} data-testid={`cuisine-card-${c.id}`} onClick={() => pick(c.id)}
                className={`group text-left rounded-2xl overflow-hidden border-2 transition-all ${selected === c.id ? "border-[#C57B57] shadow-lg" : "border-transparent hover:border-[#89937C]/60"}`}>
                <div className="relative h-32 sm:h-36 overflow-hidden">
                  <img src={c.image} alt={c.short} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
                  <div className="absolute bottom-2 left-3 right-3">
                    <div className="text-white font-serif-display text-lg leading-tight">{c.short}</div>
                    <div className="text-white/75 text-[10px] flex items-center gap-1"><Flame size={9} /> ~{c.avg_kcal} kcal/day · 7 days</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {selected === "indian" && (
          <div className="mt-6" data-testid="indian-region-picker">
            <div className="text-[10px] font-bold tracking-[0.15em] text-[#C57B57] uppercase mb-2">Indian regional style</div>
            <div className="flex flex-wrap gap-2">
              {[["", "Pan-Indian"], ...INDIAN_REGIONS].map(([id, label]) => {
                const on = region === id;
                return (
                  <button key={id || "pan"} data-testid={`indian-region-${id || "pan"}`} aria-pressed={on}
                    onClick={() => setParams(id ? { c: "indian", r: id } : { c: "indian" })}
                    className={`rounded-full border px-4 py-1.5 text-xs font-medium transition-all ${on ? "border-[#C57B57] bg-[#C57B57] text-white" : "border-[#E5E2D9] bg-white text-[#5C665A] hover:border-[#89937C]"}`}>
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {festList.length > 0 && (
          <div className="mt-12" data-testid="festival-specials">
            <div className="text-xs font-bold tracking-[0.2em] text-[#C57B57] uppercase flex items-center gap-2"><Sparkles size={13} /> Festival Specials — seasonal 7-day menus</div>
            <p className="mt-1 text-sm text-[#5C665A]">Navratri vrat, Ramadan Suhoor-Iftar and a lean Christmas week — request them in season, downloadable like any Global Series plan.</p>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {festList.map((c) => (
                <button key={c.id} data-testid={`festival-card-${c.id}`} onClick={() => setParams({ f: c.id })}
                  className={`group text-left rounded-2xl overflow-hidden border-2 transition-all ${festSel === c.id ? "border-[#C57B57] shadow-lg" : "border-transparent hover:border-[#89937C]/60"}`}>
                  <div className="relative h-36 overflow-hidden">
                    <img src={c.image} alt={c.short} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
                    <span className="absolute top-2 left-3 rounded-full bg-[#C57B57] text-white text-[9px] font-bold tracking-wide px-2 py-0.5 uppercase">Seasonal</span>
                    <div className="absolute bottom-2 left-3 right-3">
                      <div className="text-white font-serif-display text-lg leading-tight">{c.short}</div>
                      <div className="text-white/75 text-[10px] flex items-center gap-1"><Flame size={9} /> ~{c.avg_kcal} kcal/day · {c.season}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {(selected || festSel) && member === null && (
          <div className="mt-10 rounded-3xl bg-white border border-[#E5E2D9] p-8 text-center" data-testid="global-login-prompt">
            <Lock size={20} className="inline text-[#C57B57]" />
            <h2 className="mt-2 font-serif-display text-2xl">Members unlock the full plan</h2>
            <p className="mt-1 text-sm text-[#5C665A]">Log in (free) to view every meal, macros and download your branded PDF.</p>
            <a href={`/login?redirect=${encodeURIComponent(festSel ? `/diet/global-series?f=${festSel}` : `/diet/global-series?c=${selected}`)}`} className="mt-5 inline-flex rounded-full px-8 py-3 bg-[#2D372B] text-white text-sm font-medium hover:bg-[#89937C] transition-colors">Log in / Create account</a>
          </div>
        )}

        {loading && <div className="py-16 text-center"><Loader2 className="animate-spin inline text-[#89937C]" /></div>}

        {detail && !loading && (
          <div className="mt-12" data-testid="meal-plan-viewer">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h2 className="font-serif-display text-3xl">{detail.name}</h2>
                <p className="text-sm text-[#5C665A] mt-1">{detail.tagline}</p>
              </div>
              <button data-testid="global-pdf-btn" onClick={downloadPdf} disabled={pdfBusy}
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 bg-[#C57B57] text-white text-sm font-medium hover:bg-[#AD6A48] transition-colors disabled:opacity-60">
                {pdfBusy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Download 7-Day Plan + Grocery PDF
              </button>
            </div>

            <div className="mt-6 flex gap-2 overflow-x-auto pb-1" data-testid="day-tabs">
              {detail.plan.map((d) => (
                <button key={d.day} data-testid={`day-tab-${d.day}`} onClick={() => setDay(d.day)}
                  className={`shrink-0 rounded-full px-5 py-2 text-sm font-medium transition-colors ${day === d.day ? "bg-[#2D372B] text-white" : "bg-white border border-[#E5E2D9] text-[#5C665A] hover:border-[#89937C]"}`}>
                  Day {d.day}
                </button>
              ))}
            </div>

            {activeDay && (
              <>
                <div className="mt-5 rounded-2xl bg-[#2D372B] text-[#F7F5F0] px-6 py-4 flex flex-wrap items-center gap-x-8 gap-y-2" data-testid="day-totals">
                  <span className="font-serif-display text-lg">Day {activeDay.day} totals</span>
                  <span className="text-sm"><span className="text-[#C57B57] font-bold">{activeDay.totals.kcal}</span> kcal</span>
                  <span className="text-sm">Protein <b>{activeDay.totals.protein}g</b></span>
                  <span className="text-sm">Carbs <b>{activeDay.totals.carbs}g</b></span>
                  <span className="text-sm">Fat <b>{activeDay.totals.fat}g</b></span>
                </div>
                <div className="mt-4 grid sm:grid-cols-2 gap-4">
                  {SLOTS.map((slot) => {
                    const m = activeDay.meals[slot];
                    return (
                      <div key={slot} className="bg-white border border-[#E5E2D9] rounded-2xl p-5" data-testid={`meal-${slot}`}>
                        <div className="text-[10px] font-bold tracking-[0.15em] text-[#C57B57] uppercase">{detail.slot_labels?.[slot] || SLOT_LABEL[slot]}</div>
                        <div className="mt-1 font-serif-display text-lg">{m.name}</div>
                        <ul className="mt-2 text-xs text-[#5C665A] space-y-0.5">
                          {m.items.map((it, i) => <li key={i}>• {it}</li>)}
                        </ul>
                        <div className="mt-3"><MacroChips m={m} /></div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            <div className="mt-10 bg-white border border-[#E5E2D9] rounded-3xl p-7" data-testid="grocery-list">
              <h3 className="font-serif-display text-2xl flex items-center gap-2"><ShoppingBasket size={18} className="text-[#89937C]" /> 7-Day Grocery List</h3>
              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {Object.entries(detail.grocery).map(([cat, items]) => (
                  <div key={cat}>
                    <div className="text-[10px] font-bold tracking-[0.15em] text-[#C57B57] uppercase mb-1.5">{cat}</div>
                    <ul className="text-xs text-[#5C665A] space-y-1">{items.map((it, i) => <li key={i}>• {it}</li>)}</ul>
                  </div>
                ))}
              </div>
            </div>
            <p className="mt-6 text-[10px] text-[#9AA294]">Fat-loss baseline ~1,400–1,550 kcal/day — Coach Jay can personalise portions. Food nutrition search powered by <a href="https://www.fatsecret.com" target="_blank" rel="noreferrer" className="underline">FatSecret</a>. General guidance, not medical advice.</p>
          </div>
        )}
      </div>
    </div>
  );
}
